import React from "react";
import {
	Button,
	ClassNameProps,
	Conditional,
	Else,
	If,
	makeClassName,
	Modal,
	Row,
	StyleProps,
} from "@hex-insights/core";
import { Badge } from "../../../../Components/Badge";
import { Icon, IconContainer } from "../../../../Components/Icon";
import { Student, StudentNameFragment, StudentOTCMedicationDetailFragment } from "../../../../Utilities";
import { StudentOTCMedicationStudentListModal } from "../StudentListModal";
import styles from "./styles.module.css";

export type StudentOTCMedicationStudentOverviewCardProps = {
	isLoading?: boolean;
	student: Pick<Student, "id"> & StudentNameFragment;
	studentOTCMedications: Omit<StudentOTCMedicationDetailFragment, "student">[] | null | undefined;
} & Partial<ClassNameProps & StyleProps>;

export function StudentOTCMedicationStudentOverviewCard({
	isLoading = false,
	student,
	studentOTCMedications,
	className,
	style,
}: StudentOTCMedicationStudentOverviewCardProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { numAllowed, numNotAllowed } = React.useMemo(() => {
		let numAllowed = 0;
		let numNotAllowed = 0;

		if (!studentOTCMedications) {
			return { numAllowed, numNotAllowed };
		}

		for (let i = 0; i < studentOTCMedications.length; i++) {
			const studentOTCMedication = studentOTCMedications[i];
			if (studentOTCMedication.isAllowed) {
				numAllowed++;
			} else {
				numNotAllowed++;
			}
		}

		return { numAllowed, numNotAllowed };
	}, [studentOTCMedications]);

	return (
		<React.Fragment>
			<Button onClick={openModal} className={makeClassName("button--tile", className)} style={style}>
				<Row justify="space-between" align="center">
					<Row justify="spaced-start" align="center">
						<IconContainer color="var(--theme-colors__purple--background)">
							<Icon.Pill color="var(--theme-colors__purple)" size={16} />
						</IconContainer>

						<span className={styles["title"]}>OTC Medications</span>
					</Row>

					<Row justify="spaced-start" style={{ marginLeft: "0.5rem" }}>
						<Conditional>
							<If condition={isLoading && !studentOTCMedications}>Loading...</If>
							<Else>
								<If condition={numAllowed > 0 || true}>
									<Badge
										backgroundColor={
											numAllowed > 0 ? "var(--theme-colors__green)" : "var(--neutral-colors--quaternary)"
										}
										className={styles["summary-value"]}
									>
										<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
											<Icon.Check /> {numAllowed}
										</Row>
									</Badge>
								</If>
								<If condition={numNotAllowed > 0 || true}>
									<Badge
										backgroundColor={
											numNotAllowed > 0 ? "var(--theme-colors__pink)" : "var(--neutral-colors--quaternary)"
										}
										className={styles["summary-value"]}
									>
										<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
											<Icon.X /> {numNotAllowed}
										</Row>
									</Badge>
								</If>
							</Else>
						</Conditional>
					</Row>
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentOTCMedicationStudentListModal
					isLoading={isLoading}
					student={student}
					studentOTCMedications={studentOTCMedications}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}
